// src/pages/servicos/cadastros/EquipamentoClienteCad.js
import React, { useEffect, useState } from 'react';

import Input from '../../../components/input/Input';
import InputCheck from '../../../components/input/InputCheck';
import ModalBody from '../../../components/modalForm/ModalBody';
import ModalContainer from '../../../components/modalForm/ModalContainer';
import ModalFooter from '../../../components/modalForm/ModalFooter';
import ModalHeader from '../../../components/modalForm/ModalHeader';
import { useMessage } from '../../../contexts/geral/MessageContext';
import { useYesNo } from '../../../contexts/geral/YesNoContext';
import { useCliente } from '../../../contexts/pessoas/ClienteContext';
import { useMarca } from '../../../contexts/produtos/MarcaContext';
import { useEquipamentoCliente } from '../../../contexts/servicos/EquipamentoClienteContext';
import { useTipoEquipamento } from '../../../contexts/servicos/TipoEquipamentoContext';
import { EquipamentoCliente } from '../../../models/servicos/EquipamentoCliente';
import ModalActions, { ModalActionButton } from '../../../components/modalForm/ModalActions';

const EquipamentoClienteCad = ({ show, onHide, initialEquipamento, initialCliente, onSaveEquipamento }) => {
  const [equipamento, setEquipamento] = useState(new EquipamentoCliente());
  const [novoRegistro, setNovoRegistro] = useState(true);
  const { clientes, showClienteSearch, showClienteCad } = useCliente();
  const { marcas } = useMarca();
  const { tiposEquipamento, listarTiposEquipamento } = useTipoEquipamento();
  const {
    equipamentos,
    listarEquipamentos,
    adicionarEquipamento,
    atualizarEquipamento,
    excluirEquipamento,
    handleShowEquipamentoClienteSearch,
  } = useEquipamentoCliente();
  const { showMessage } = useMessage();
  const { showYesNo } = useYesNo();
  const clienteBloqueado = Boolean(initialCliente?.id);
  const clienteInicialNome = initialCliente?.pessoa?.nome || initialCliente?.nome || '';

  const novoEquipamento = () => ({
    ...new EquipamentoCliente(),
    ...(clienteBloqueado ? {
      clienteId: initialCliente.id,
      clienteNome: clienteInicialNome,
    } : {}),
  });

  const handleNovo = () => {
    setEquipamento(novoEquipamento());
    setNovoRegistro(true);
  };

  useEffect(() => {
    if (!show) return;
    listarTiposEquipamento(true);
    listarEquipamentos(initialCliente?.id);
    if (initialEquipamento?.id) {
      setEquipamento({
        ...new EquipamentoCliente(),
        ...initialEquipamento,
        ...(clienteBloqueado ? {
          clienteId: initialCliente.id,
          clienteNome: clienteInicialNome,
        } : {}),
      });
      setNovoRegistro(false);
    } else {
      handleNovo();
    }
  }, [show, initialEquipamento]);

  const selecionarCliente = cliente => {
    if (!cliente?.id) return;
    setEquipamento(anterior => ({
      ...anterior,
      clienteId: cliente.id,
      clienteNome: cliente.pessoa?.nome || cliente.nome || '',
    }));
  };

  const handleClienteExit = (clienteId, clienteFallback = null) => {
    if (!clienteId) {
      setEquipamento(anterior => ({ ...anterior, clienteId: 0, clienteNome: '' }));
      return;
    }
    const clienteSelecionado = clienteFallback || clientes.find(
      item => String(item.id) === String(clienteId),
    );
    if (clienteSelecionado) {
      selecionarCliente(clienteSelecionado);
    } else {
      showMessage('Cliente não encontrado!', 'warning');
      setEquipamento(anterior => ({ ...anterior, clienteId: 0, clienteNome: '' }));
    }
  };

  const handleBuscarCliente = async () => {
    const clienteSelecionado = await showClienteSearch();
    if (clienteSelecionado?.id) handleClienteExit(clienteSelecionado.id, clienteSelecionado);
  };

  const handleAddCliente = async () => {
    const clienteSelecionado = clientes.find(item => String(item.id) === String(equipamento.clienteId));
    const clienteSalvo = await showClienteCad(clienteSelecionado, false);
    if (clienteSalvo?.id) handleClienteExit(clienteSalvo.id, clienteSalvo);
  };

  const handleSelecionarEquipamento = async () => {
    const selecionado = await handleShowEquipamentoClienteSearch(equipamento.clienteId || undefined);
    if (selecionado?.id) {
      setEquipamento({ ...new EquipamentoCliente(), ...selecionado });
      setNovoRegistro(false);
    }
  };

  const handleEquipamentoExit = () => {
    if (!equipamento.id) return;
    const selecionado = equipamentos.find(item => Number(item.id) === Number(equipamento.id));
    if (selecionado) {
      setEquipamento({ ...new EquipamentoCliente(), ...selecionado });
      setNovoRegistro(false);
    } else {
      showMessage('Equipamento não encontrado!', 'warning');
      handleNovo();
    }
  };

  const validar = () => {
    if (!equipamento.clienteId) return 'Informe o cliente.';
    if (!equipamento.tipoEquipamentoId) return 'Informe o tipo de equipamento.';
    if (!equipamento.descricao?.trim()) return 'Informe a descrição/identificação.';
    return '';
  };

  const handleSave = async () => {
    const mensagem = validar();
    if (mensagem) {
      showMessage(mensagem, 'warning');
      return;
    }
    const salvo = equipamento.id
      ? await atualizarEquipamento(equipamento)
      : await adicionarEquipamento(equipamento);
    if (!salvo) return;
    onSaveEquipamento?.(salvo);
    handleNovo();
  };

  const handleDelete = async () => {
    if (!equipamento.id) {
      showMessage('Selecione um equipamento para excluir!', 'warning');
      return;
    }
    const confirmar = await showYesNo('Deseja excluir o equipamento selecionado?');
    if (confirmar) {
      const excluido = await excluirEquipamento(equipamento.id);
      if (excluido) handleNovo();
    }
  };

  if (!show) return null;

  return (
    <ModalContainer show={show} largura="820px" variant="cadastro" onHide={onHide}>
      <ModalHeader title={novoRegistro ? 'Novo Equipamento do Cliente' : 'Alterar Equipamento do Cliente'} onHide={onHide} />
      <ModalBody>
        <form>
          {clienteBloqueado && (
            <div className="border rounded bg-light px-3 py-2 mb-3">
              <small className="d-block text-muted">Cliente do equipamento</small>
              <span className="fs-5 fw-semibold">#{initialCliente.id} — {clienteInicialNome}</span>
            </div>
          )}
          <div className="d-flex gap-2 flex-wrap mb-2">
            <Input
              label="Código"
              value={equipamento.id || ''}
              onChange={e => setEquipamento({ ...equipamento, id: Number(e.target.value) || 0 })}
              onBlur={handleEquipamentoExit}
              disabled={!novoRegistro}
              btnBuscar={handleSelecionarEquipamento}
              size={100}
            />
            {!clienteBloqueado && (
              <>
                <Input
                  label="Cliente"
                  value={equipamento.clienteId || ''}
                  onChange={e => setEquipamento({ ...equipamento, clienteId: Number(e.target.value) || 0 })}
                  onBlur={e => handleClienteExit(e.target.value)}
                  btnBuscar={handleBuscarCliente}
                  btnAdd={handleAddCliente}
                  size={100}
                />
                <Input
                  label="Descrição do cliente"
                  value={equipamento.clienteNome || ''}
                  disabled
                  size={430}
                />
              </>
            )}
          </div>
          <div className="row">
            <div className="col-md-4">
              <label className="form-label">Tipo</label>
              <select
                className="form-select"
                value={equipamento.tipoEquipamentoId || 0}
                onChange={e => setEquipamento({ ...equipamento, tipoEquipamentoId: Number(e.target.value) })}
              >
                <option value="0">Selecione</option>
                {tiposEquipamento.map(tipo => <option key={tipo.id} value={tipo.id}>{tipo.descricao}</option>)}
              </select>
            </div>
            <div className="col-md-8">
              <Input
                label="Descrição/identificação"
                value={equipamento.descricao || ''}
                size="100%"
                autoFocus
                onChange={e => setEquipamento({ ...equipamento, descricao: e.target.value })}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-4">
              <label className="form-label">Marca</label>
              <select
                className="form-select"
                value={equipamento.marcaId || ''}
                onChange={e => setEquipamento({ ...equipamento, marcaId: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">Não informada</option>
                {marcas.map(marca => <option key={marca.id} value={marca.id}>{marca.descricao}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <Input label="Modelo" value={equipamento.modelo || ''} size="100%" onChange={e => setEquipamento({ ...equipamento, modelo: e.target.value })} />
            </div>
            <div className="col-md-4">
              <Input label="Número de série" value={equipamento.numeroSerie || ''} size="100%" onChange={e => setEquipamento({ ...equipamento, numeroSerie: e.target.value })} />
            </div>
          </div>
          <div className="row">
            <div className="col-md-4">
              <Input label="Patrimônio/placa" value={equipamento.patrimonio || ''} size="100%" onChange={e => setEquipamento({ ...equipamento, patrimonio: e.target.value })} />
            </div>
            <div className="col-md-4">
              <Input label="Cor" value={equipamento.cor || ''} size="100%" onChange={e => setEquipamento({ ...equipamento, cor: e.target.value })} />
            </div>
            <div className="col-md-4">
              <Input label="Ano" type="number" value={equipamento.anoFabricacao || ''} size="100%" onChange={e => setEquipamento({ ...equipamento, anoFabricacao: e.target.value ? Number(e.target.value) : null })} />
            </div>
          </div>
          <Input label="Observação" value={equipamento.observacao || ''} rows={2} size="100%" onChange={e => setEquipamento({ ...equipamento, observacao: e.target.value })} />
          <InputCheck id="equipamento-cliente-ativo" label="Ativo" checked={equipamento.ativo} onChange={e => setEquipamento({ ...equipamento, ativo: e.target.checked })} />
        </form>
      </ModalBody>
      <ModalFooter>
        <ModalActions >
          <ModalActionButton type="button" className="btn btn-primary" onClick={handleSave} action="save">Salvar </ModalActionButton>
          <ModalActionButton type="button" className="btn btn-danger" onClick={handleDelete} disabled={novoRegistro} action="delete">Excluir </ModalActionButton>
          <ModalActionButton type="button" className="btn btn-secondary" onClick={handleNovo} disabled={novoRegistro} action="new">Novo </ModalActionButton>
        </ModalActions>
      </ModalFooter>
    </ModalContainer>
  );
};

export default EquipamentoClienteCad;
